import { type FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import MediaFileUploadBox from './MediaFileUploadBox.tsx'
import { uploadFile } from '@/features/media-file/domain/use-cases/upload-file.ts'
import Select from '../../common/Select.tsx'
import Button from '../../common/Button.tsx'
import { handleKeyDown } from '@/utils/handle-key-down.ts'
import { QueryKeys } from '@/utils/query-keys.ts'
import type { UUID } from '@/features/shared/data/string.types.ts'
import getCategories from '@/features/media-file/domain/use-cases/get-categories.ts'
import type { MediaCategory } from '@/features/media-file/data/dto/media-category.ts'

interface MediaFileUploadFormProps {
  file: File
  folderId: UUID
  onClose: () => void
}

type Form = { name: string; category: UUID | undefined }
type FormErrors = { name?: string; category?: string }
type FormTouch = { name: boolean; category: boolean }

const MediaFileUploadForm: FC<MediaFileUploadFormProps> = ({
  file,
  folderId,
  onClose,
}) => {
  const { t } = useTranslation()
  const [form, setForm] = useState<Form>({
    name: file.name,
    category: undefined,
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSaving, setIsSaving] = useState(false)
  const [categories, setCategories] = useState<MediaCategory[]>([])
  const [touched, setTouched] = useState<FormTouch>({
    name: false,
    category: false,
  })
  const queryClient = useQueryClient()

  const validateForm = (form: Form) => {
    const newErrors: FormErrors = {}
    if (!form.name) newErrors.name = t('ls_upload_file_form_name_required')
    if (!form.category) {
      newErrors.category = t('ls_upload_file_form_category_required')
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  useEffect(() => {
    validateForm(form)
  }, [form])

  useEffect(() => {
    getCategories().then(setCategories)
  }, [])

  const onUpload = async (file: File, categoryId: UUID): Promise<string> => {
    setIsSaving(true)
    const categoryName = categories.find(
      ({ categoryId: id }) => id === categoryId
    )?.name
    if (!categoryName) {
      throw new Error('Category not found')
    }
    return uploadFile(
      file,
      folderId,
      ensureFileExtension(file, form.name),
      categoryId,
      categoryName
    )
  }

  const mutation = useMutation({
    mutationFn: ({ file, categoryId }: { file: File; categoryId: UUID }) => {
      return onUpload(file, categoryId)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.MediaFolderContent],
      })
      onClose()
      setIsSaving(false)
    },
    onError: (error) => {
      setIsSaving(false)
      onClose()
      console.error('Error creating script:', error)
    },
  })

  const ensureFileExtension = (file: File, filename: string): string => {
    const fileExtension = file.name.split('.').pop()
    const hasExtension = /\.[a-zA-Z0-9]+$/.test(filename)

    if (hasExtension) return filename

    return `${filename}.${fileExtension}`
  }

  return (
    <div className="inline-flex h-fit w-full flex-col items-start justify-start gap-6 p-4 pt-6">
      <div className="inline-flex items-center justify-center gap-2 self-stretch">
        <MediaFileUploadBox file={file} />
      </div>
      <div className="flex w-full flex-col gap-2 text-sm">
        <label className="font-semibold" htmlFor="name">
          File Name
        </label>
        <input
          type="text"
          id="name"
          className={`font-sm h-9 w-full gap-2 rounded border bg-transparent px-4 py-2 font-medium text-white ${
            touched.name && errors.name ? 'border-red-500' : 'border-zinc-500'
          }`}
          value={form.name}
          onKeyDown={handleKeyDown}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
        />
        {touched.name && errors.name && (
          <div className="text-xs font-normal leading-none text-red-500">
            {errors.name}
          </div>
        )}
      </div>
      <div className="flex w-full flex-col gap-2 text-sm">
        <label className="font-semibold" htmlFor="category">
          {t('ls_upload_file_select_category')}
        </label>
        <Select
          type="field"
          options={categories.map(({ categoryId, name }) => ({
            value: categoryId,
            label: name.replace(/\s+/g, '').toLocaleLowerCase(),
          }))}
          filterNameKey="ls_upload_file_select_one"
          onChange={(value) => {
            setForm({ ...form, category: value as UUID })
            setTouched((prev) => ({ ...prev, category: true }))
          }}
          error={touched.category ? errors.category : undefined}
        />
        {touched.category && errors.category && (
          <div className="text-xs font-normal leading-none text-red-500">
            {errors.category}
          </div>
        )}
      </div>
      <div className="inline-flex items-center justify-end gap-4 self-stretch">
        <Button onClick={onClose}>{t('ls_new_script_cancel')}</Button>
        <Button
          isLoading={isSaving}
          onClick={() => {
            const isValid = validateForm(form)
            setTouched({ name: true, category: true })
            if (isValid) {
              mutation.mutate({
                file: file,
                categoryId: form.category!,
              })
            }
          }}
        >
          {t('ls_new_script_save')}
        </Button>
      </div>
    </div>
  )
}

export default MediaFileUploadForm
